"use client"

import { useState } from "react"
import { PageAction } from "@/components/graphite/page-header"
import { TabHeader, TabSurface, TonePill } from "@/components/graphite/surface"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { stageHex } from "@/lib/stage-colors"
import { Plus, Trash2 } from "lucide-react"
import { useDeleteOrgObsUsersRevoke, useGetOrgObsUsers, usePutOrgObsUsersRole } from "openapi/api/endpoints"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"

export const ManageOrg = ({ inviteTrigger }: { inviteTrigger: () => void }) => {
  const { data: members, refetch} = useGetOrgObsUsers()
  const [updatingRole, setUpdatingRole] = useState<string | null>(null)
  const {mutateAsync:adjustRole}=usePutOrgObsUsersRole()
  const {mutateAsync:deleteUser}=useDeleteOrgObsUsersRevoke()
  const queryClient=useQueryClient()
  const isAdmin=!!members?.length


  const handleRoleChange = async (permissionId: string, newRole: string) => {
    setUpdatingRole(permissionId)
    try {
        await adjustRole({data:{
            permission_id:permissionId,
            role:newRole as 'admin' | 'user'
        }})
        await queryClient.invalidateQueries({queryKey:[`/orgObs/users`],exact:false})
        await refetch()
        toast.success('User role Update successfully')
    } catch (error) {
        toast.error("An error occured!",{
            description: "Failed to adjust user roles",
            action: {
              label: "Dismiss",
              onClick: () => {},
            },
            style: {
              background: "#dc2626",
            },
          });
    } finally {
      setUpdatingRole(null)
    }
  }

  const handleDelete = async (permissionId: string) => {
    setUpdatingRole(permissionId)
    try {
        await deleteUser({data:{
            permission_id:permissionId
        }})
        await queryClient.invalidateQueries({queryKey:[`/orgObs/users`]})
        await refetch()
        toast.success('User role Update successfully')

      // Optionally refetch members or update local state
    } catch (error) {
        toast.error("An error occured!",{
            description: "Failed to delete user",
            action: {
              label: "Dismiss",
              onClick: () => {},
            },
            style: {
              background: "#dc2626",
            },
          });
    } finally {
      setUpdatingRole(null)
    }
  }

  if (!isAdmin) {
    return (
      <TabSurface fill={false}>
        <TabHeader label="Team" />
        <p className="pt-4 text-[12.5px] leading-[1.5] text-muted-foreground">
          Team management is reserved to admins.
        </p>
      </TabSurface>
    )
  }

  return (
    <TabSurface fill={false}>
      <TabHeader
        label="Team"
        count={members?.length}
        actions={
          <PageAction tone="primary" onClick={inviteTrigger}>
            <Plus className="size-[15px]" />
            Invite Member
          </PageAction>
        }
      />
      {/*
        Hairline-separated rows inside the one surface, not a stack of bordered
        boxes inside a card — a surface does not hold surfaces. The row stacks
        below `@lg` because the pane it lives in is narrow far more often than
        the window is, which is what the old `sm:` breakpoints got wrong: they
        asked the viewport, which is wide, about a column that is not.
      */}
      <div className="divide-y">
        {members?.map((member) => (
          <div
            key={member.email}
            className="flex flex-col gap-3 py-3.5 @lg:flex-row @lg:items-center @lg:gap-4"
          >
            <div className="flex min-w-0 items-center gap-2.5">
              <Avatar className="size-8 flex-none border">
                <AvatarFallback className="text-[11px]">
                  {(member.user ?? (member.email as string))
                    .split(" ")
                    .map((part) => part[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[12.5px] font-medium leading-[1.3]">
                  {member.user ?? "—"}
                </p>
                <p className="truncate text-[11.5px] leading-[1.4] text-muted-foreground">
                  {member.email}
                </p>
              </div>
            </div>
            <div className="flex flex-none items-center gap-2.5 @lg:ml-auto">
              <TonePill
                hex={stageHex(member.status === "active" ? "hired" : "screening")}
                compact
              >
                {member.status === "active" ? "Active" : "Pending"}
              </TonePill>
              <Select
                defaultValue={member.role}
                onValueChange={(value) => handleRoleChange(member.id as string, value)}
                disabled={updatingRole === member.id}
              >
                <SelectTrigger className="h-8 w-[118px] text-[12px]">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="user">Recruiter</SelectItem>
                </SelectContent>
              </Select>
              <button
                type="button"
                aria-label={`Remove ${member.user ?? member.email}`}
                onClick={() => handleDelete(member.id as string)}
                disabled={updatingRole === member.id}
                className="flex size-8 flex-none items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-destructive disabled:opacity-50"
              >
                <Trash2 className="size-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </TabSurface>
  )
}